import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { INTEGRATION_SPECS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plug, Loader2, CheckCircle2, XCircle, Save, Activity, Sparkles, ChevronDown, Eye, EyeOff, AlertTriangle, Stethoscope } from "lucide-react";

const CARRIER_PROVIDERS = ["fedex", "rl", "priority1", "warp", "globaltranz"];

function healthOf(d) {
  if (!d?.enabled) return { tone: "off", label: "Off", color: "#71717a" };
  const st = d.status;
  if (!st || st.connected === undefined || st.connected === null)
    return { tone: "amber", label: "Untested", color: "hsl(var(--warning))" };
  if (st.connected) return { tone: "green", label: "Live", color: "hsl(var(--success))" };
  return { tone: "red", label: "Error", color: "hsl(var(--destructive))" };
}

function relTime(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function CarrierHealthStrip({ specs, byProvider }) {
  const rows = specs.filter((s) => CARRIER_PROVIDERS.includes(s.provider));
  const liveCount = rows.filter((s) => healthOf(byProvider[s.provider]).tone === "green").length;
  return (
    <div className="border border-border bg-card p-4" data-testid="carrier-health-strip">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-[hsl(var(--primary))]" />
          <h2 className="font-heading text-sm font-bold uppercase tracking-wide">Carrier Health</h2>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="carrier-health-summary">
          {liveCount} of {rows.length} live rate feeds flowing
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map((spec) => {
          const d = byProvider[spec.provider] || {};
          const h = healthOf(d);
          const when = relTime(d.status?.tested_at);
          return (
            <div
              key={spec.provider}
              title={d.status?.message || (h.tone === "off" ? "Disabled" : "Not tested yet")}
              data-testid={`carrier-health-${spec.provider}`}
              className="flex items-center gap-2 rounded-full border border-border bg-secondary/40 py-1.5 pl-2.5 pr-3"
            >
              <span className="relative flex h-2.5 w-2.5">
                {h.tone === "green" && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: h.color }} />}
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: h.color }} />
              </span>
              <span className="text-xs font-semibold">{spec.name}</span>
              <span className="text-[11px] font-medium" style={{ color: h.color }}>{h.label}</span>
              {when && <span className="text-[10px] text-muted-foreground">· {when}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IntegrationCard({ spec, data, onSaved }) {
  const [fields, setFields] = useState(data.fields || {});
  const [enabled, setEnabled] = useState(!!data.enabled);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(data.status || null);
  const [previewing, setPreviewing] = useState(false);
  const [ssResult, setSsResult] = useState(null);
  const [doctoring, setDoctoring] = useState(false);
  const [doctorResult, setDoctorResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [revealed, setRevealed] = useState({});
  const advancedFields = spec.fields.filter((f) => f.advanced);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const renderField = (f) => {
    if (f.type === "toggle") {
      const checked = fields[f.key] === undefined ? !!f.default : ["true", "1", "yes", "on", true].includes(typeof fields[f.key] === "string" ? fields[f.key].toLowerCase() : fields[f.key]);
      return (
        <div key={f.key} className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5 sm:col-span-2">
          <div>
            <Label className="label-caps">{f.label}</Label>
            {f.help && <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{f.help}</p>}
          </div>
          <Switch
            checked={checked}
            onCheckedChange={(v) => set(f.key, v ? "true" : "false")}
            data-testid={`integration-${spec.provider}-${f.key}`}
          />
        </div>
      );
    }
    return (
      <div key={f.key} className={spec.fields.filter((x) => !x.advanced).length === 1 ? "sm:col-span-2" : ""}>
        <Label className="label-caps">
          {f.label}
          {f.required && <span className="ml-1 text-[hsl(var(--destructive))]">*</span>}
        </Label>
        <div className="relative mt-1.5">
          <Input
            type={f.secret && !revealed[f.key] ? "password" : f.type === "number" ? "number" : "text"}
            value={fields[f.key] ?? (f.default !== undefined ? String(f.default) : "")}
            onChange={(e) => set(f.key, e.target.value)}
            className={`h-10 ${f.secret ? "pr-10" : ""}`}
            data-testid={`integration-${spec.provider}-${f.key}`}
            placeholder={f.secret ? "••••••••" : ""}
            autoComplete="off"
          />
          {f.secret && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setRevealed((r) => ({ ...r, [f.key]: !r[f.key] }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid={`integration-${spec.provider}-${f.key}-reveal`}
              aria-label="Toggle visibility"
            >
              {revealed[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {f.secret && data.secret_hints?.[f.key] && (
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground" data-testid={`integration-${spec.provider}-${f.key}-hint`}>
            Stored: {data.secret_hints[f.key]}
          </p>
        )}
        {f.help && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{f.help}</p>}
      </div>
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/${spec.provider}`, { enabled, fields });
      toast.success(`${spec.name} saved`);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.put(`/integrations/${spec.provider}`, { enabled, fields }); // persist before testing
      const { data: res } = await api.post(`/integrations/${spec.provider}/test`);
      setStatus({ connected: res.success, message: res.message, tested_at: new Date().toISOString() });
      res.success ? toast.success(res.message) : toast.warning(res.message);
    } catch (e) {
      const msg = apiError(e);
      setStatus({ connected: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const previewAccessorials = async () => {
    setPreviewing(true);
    setSsResult(null);
    try {
      await api.put(`/integrations/${spec.provider}`, { enabled, fields }); // persist before previewing
      const { data: res } = await api.post(`/integrations/fedex/special-services`, {});
      setSsResult(res);
      res.success ? toast.success(res.message) : toast.warning(res.message);
    } catch (e) {
      const msg = apiError(e);
      setSsResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setPreviewing(false);
    }
  };

  const runDoctor = async () => {
    setDoctoring(true);
    setDoctorResult(null);
    try {
      await api.put(`/integrations/${spec.provider}`, { enabled, fields }); // persist before diagnosing
      const { data: res } = await api.post(`/integrations/fedex/doctor`, {});
      setDoctorResult(res);
      res.success ? toast.success(res.summary) : toast.warning(res.summary);
    } catch (e) {
      const msg = apiError(e);
      setDoctorResult({ success: false, summary: msg, checks: [] });
      toast.error(msg);
    } finally {
      setDoctoring(false);
    }
  };

  return (
    <div className="border border-border bg-card" data-testid={`integration-${spec.provider}`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-md" style={{ background: spec.color }}>
            <Plug className="h-4 w-4 text-white" />
          </span>
          <div>
            <h3 className="font-heading text-base font-bold leading-tight tracking-tight">{spec.name}</h3>
            <p className="text-xs text-muted-foreground">{spec.blurb}</p>
          </div>
        </div>
        <label className="flex items-center gap-2">
          <span className="label-caps">Enabled</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`integration-${spec.provider}-enabled`} />
        </label>
      </div>

      {status && (
        <div className={`flex items-center gap-2 border-b border-border px-4 py-2 text-sm ${status.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
          {status.connected ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {status.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
        {spec.fields.filter((f) => !f.advanced).map(renderField)}
      </div>

      {advancedFields.length > 0 && (
        <div className="border-t border-border px-4 pb-4">
          <button
            type="button"
            onClick={() => setShowAdvanced((s) => !s)}
            className="flex w-full items-center gap-1.5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
            data-testid={`integration-${spec.provider}-advanced-toggle`}
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? "rotate-0" : "-rotate-90"}`} />
            Advanced settings — pre-filled defaults (optional)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`integration-${spec.provider}-advanced`}>
              {advancedFields.map(renderField)}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-border p-4">
        <Button onClick={save} disabled={saving} data-testid={`integration-${spec.provider}-save`} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Save</>}
        </Button>
        <Button variant="outline" onClick={test} disabled={testing} data-testid={`integration-${spec.provider}-test`}>
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Activity className="mr-1.5 h-4 w-4" />Test Connection</>}
        </Button>
        {spec.provider === "fedex" && (
          <Button variant="ghost" onClick={previewAccessorials} disabled={previewing} data-testid="integration-fedex-special-services">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" />Preview Accessorials</>}
          </Button>
        )}
        {spec.provider === "fedex" && (
          <Button variant="ghost" onClick={runDoctor} disabled={doctoring} data-testid="integration-fedex-doctor">
            {doctoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Stethoscope className="mr-1.5 h-4 w-4" />Booking Doctor</>}
          </Button>
        )}
      </div>

      {spec.provider === "fedex" && ssResult && (
        <div className="border-t border-border px-4 py-3 text-xs" data-testid="integration-fedex-special-services-result">
          <div className={`flex items-center gap-2 ${ssResult.success ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
            {ssResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            <span>{ssResult.message}</span>
          </div>
          {ssResult.options?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ssResult.options.map((o) => (
                <span key={o} className="rounded border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{o}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {spec.provider === "fedex" && doctorResult && (
        <div className="border-t border-border px-4 py-3" data-testid="integration-fedex-doctor-result">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Stethoscope className="h-4 w-4 text-[hsl(var(--primary))]" />
            <span className={doctorResult.success ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>
              {doctorResult.summary}
            </span>
          </div>
          <div className="space-y-1.5">
            {(doctorResult.checks || []).map((c, i) => (
              <div key={i} className="rounded border border-border bg-muted/40 px-3 py-2 text-xs" data-testid={`doctor-check-${i}`}>
                <div className="flex items-center gap-2">
                  {c.status === "pass" ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" />
                    : c.status === "fail" ? <XCircle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--destructive))]" />
                      : <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--warning))]" />}
                  <span className="font-semibold">{c.step}</span>
                </div>
                <div className="mt-1 pl-5 text-muted-foreground">{c.detail}</div>
                {c.fix && <div className="mt-1 pl-5 font-medium text-[hsl(var(--primary))]">&rarr; Fix: {c.fix}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminNotifications() {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/notifications")
      .then((r) => { setTo(r.data.to || ""); setCc((r.data.cc || []).join(", ")); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await api.put("/settings/notifications", { to: to.trim(), cc: ccList });
      setTo(r.data.to); setCc((r.data.cc || []).join(", "));
      toast.success("Notification recipients saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-card" data-testid="admin-notifications-card">
      <div className="border-b border-border px-4 py-3">
        <h2 className="font-heading text-lg font-bold tracking-tight">Admin Notification Recipients</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Where &quot;New Dev Request&quot; and other admin alerts are emailed. Change it here anytime — no code deploy needed.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
        <div>
          <Label className="label-caps">Primary Recipient</Label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="josh@phsinc.com" disabled={loading}
            data-testid="notify-to-input" className="mt-1 h-9" />
        </div>
        <div>
          <Label className="label-caps">CC (optional, comma-separated)</Label>
          <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="ops@phsinc.com, sales@phsinc.com" disabled={loading}
            data-testid="notify-cc-input" className="mt-1 h-9" />
        </div>
      </div>
      <div className="flex justify-end border-t border-border px-4 py-3">
        <Button onClick={save} disabled={saving || loading} data-testid="notify-save" className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save Recipients
        </Button>
      </div>
    </div>
  );
}

export default function Integrations() {
  const [data, setData] = useState(null);

  const load = () => api.get("/integrations").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading integrations…</div>;

  const byProvider = Object.fromEntries(data.map((d) => [d.provider, d]));

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">Integrations & Carriers</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Store carrier and CRM API credentials. Keys are saved to the database and used by the carrier adapters.
        </p>
      </div>

      <div className="rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 p-3 text-xs text-muted-foreground">
        Credentials are stored securely in the database (admin-only). <span className="font-semibold text-foreground">FedEx Freight LTL, R+L Carriers, and Warp</span> make live API rate calls when enabled with valid credentials — &quot;Test Connection&quot; runs a real request. FedEx Freight uses Ping AIC OAuth2 client-credentials (scope-based) and the ltl.tech getRateQuote endpoint, fully separate from FedEx parcel. If a live call fails, quoting falls back to a clearly-labeled <span className="font-semibold text-foreground">PHS Estimate / Fallback</span>.
      </div>

      <CarrierHealthStrip specs={INTEGRATION_SPECS} byProvider={byProvider} />

      <AdminNotifications />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {INTEGRATION_SPECS.map((spec) => (
          <IntegrationCard key={spec.provider} spec={spec} data={byProvider[spec.provider] || { fields: {} }} onSaved={load} />
        ))}
      </div>
    </div>
  );
}
